import type {
  AnswerOption,
  Question,
  QuestionAnswer,
  QuestionOptionItem,
} from '@tabel/shared/schemas/questions';
import { UNANSWERED_STATES } from '@tabel/shared/schemas/questions';
import { type HoursFormat, hoursText, ruDate, ruDateTime, shownHours, weekday } from './format';

/*
 * Вопросы бригадиру по табелю (003, US2): подписи типов, текст вопроса простыми словами, порядок, очередь.
 * Часы интерфейс не считает: машинные часы, часы ответа и предпросмотр присылает сервер (движок).
 * Коды пометок (`A1.нет_выхода`) на экран не выходят — только подписи (FR-015).
 */

type Tone = 'bad' | 'warn';

interface TypeInfo {
  label: string;
  /** «Нет входа», «нет выхода», «>24 ч» — проблема дня: красным; остальное — «внимание». */
  tone: Tone;
  order: number;
}

/** Типы вопросов по последней части кода пометки: `A1.нет_выхода` → `нет_выхода`. */
const TYPES: Readonly<Record<string, TypeInfo>> = {
  нет_выхода: { label: 'Нет выхода', tone: 'bad', order: 0 },
  нет_входа: { label: 'Нет входа', tone: 'bad', order: 1 },
  больше_24_ч: { label: 'Больше 24 ч', tone: 'bad', order: 2 },
  потерян_проход: { label: 'Потерян проход', tone: 'warn', order: 3 },
  две_бригады: { label: 'Две бригады', tone: 'warn', order: 4 },
};

const kindOf = (type: string) => type.split('.').pop() ?? type;

function infoOf(type: string): TypeInfo {
  const kind = kindOf(type);
  const known = TYPES[kind];
  if (known) return known;
  // Новый тип без подписи: слова кода без номера кубика («нет_проходов» → «Нет проходов»).
  const words = kind.replace(/_/g, ' ').trim();
  return { label: words.charAt(0).toUpperCase() + words.slice(1), tone: 'warn', order: 99 };
}

export const typeLabel = (type: string) => infoOf(type).label;
export const typeTone = (type: string) => infoOf(type).tone;
/** Порядок типов в списке: проблемы дня первыми. */
export const typeOrder = (type: string) => infoOf(type).order;
/** Тип по последней части кода: `нет_выхода`, `две_бригады` … */
export const typeKind = kindOf;

/** Без ответа: `open`, `later`, `clarify` — их считают счётчики и правило сдачи. */
export const isUnanswered = (q: Pick<Question, 'state'>) => UNANSWERED_STATES.includes(q.state);
/** Отвечен: ответ бригадира есть (`answered`) или принят кадрами (`accepted`). */
export const isAnswered = (q: Pick<Question, 'state'>) => q.state === 'answered' || q.state === 'accepted';
/** Ответ ждёт решения кадров. */
export const toReview = (q: Pick<Question, 'state'>) => q.state === 'answered';
/**
 * Кадры могут отметить «Уточнить»: ответ ждёт решения или уже принят (`answered | accepted → clarify`, data-model,
 * «Переходы состояний»). «Принято» — только по ответу, который ждёт решения (`toReview`).
 */
export const reviewable = (q: Pick<Question, 'state'>) => q.state === 'answered' || q.state === 'accepted';

/**
 * Варианты ввода ответа: при изменении отвеченного или принятого ответа «Уточню позже» нет — отложить можно только
 * вопрос без ответа (`open | clarify → later`; сервер отклоняет `later` для вопроса с ответом).
 */
export function answerOptions(q: Pick<Question, 'state' | 'options'>): QuestionOptionItem[] {
  return isUnanswered(q) ? q.options : q.options.filter((o) => o.key !== 'later');
}

/** Ключ дня: человек и дата. */
export const dayKey = (personId: string, date: string) => `${personId}|${date}`;

/** Порядок вопросов: от старой даты к новой, по ФИО, по типу. */
export function compareQuestions(a: Question, b: Question): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  const n = a.displayName.localeCompare(b.displayName, 'ru');
  if (n !== 0) return n;
  return infoOf(a.type).order - infoOf(b.type).order;
}

export function plural(n: number, [one, few, many]: readonly [string, string, string]): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

/** «1 вопрос», «3 вопроса», «5 вопросов». */
export const nq = (n: number) => `${n} ${plural(n, ['вопрос', 'вопроса', 'вопросов'])}`;

/** «пн, 05.10». */
export const dayLabel = (iso: string) => `${weekday(iso)}, ${ruDate(iso).slice(0, 5)}`;

/** Машинные часы вопроса строкой: значение правил объекта, иначе число. */
const machine = (q: Question) => shownHours(q.facts.machineValue, q.facts.machineHours);

function firstPass(q: Question, dir: 'IN' | 'OUT'): string | null {
  return q.facts.passes.find((p) => p.direction === dir)?.time ?? null;
}

/** Проходы словами: «вход 07:50, выход 17:40, вход 23:58, выход 15:30 следующего дня». */
function passesText(q: Question): string {
  return q.facts.passes
    .map((p) => {
      const what = p.direction === 'IN' ? 'вход' : p.direction === 'OUT' ? 'выход' : 'проход';
      return `${what} ${p.time}${p.nextDay ? ' следующего дня' : ''}`;
    })
    .join(', ');
}

/** Вопрос простыми словами с фактами дня (FR-015; черновик требований, «Типы вопросов»). */
export function questionText(q: Question): string {
  const d = ruDate(q.date).slice(0, 5);
  const h = machine(q);
  switch (kindOf(q.type)) {
    case 'нет_выхода': {
      const t = firstPass(q, 'IN');
      return `${t ? `Вход в ${t}, выхода нет.` : 'Выхода нет.'} Когда был выход?`;
    }
    case 'нет_входа': {
      const t = firstPass(q, 'OUT');
      return `${t ? `Выход в ${t}, входа нет.` : 'Входа нет.'} Когда был вход?`;
    }
    case 'больше_24_ч':
      return `За ${d} по проходам выходит больше 24 ч: ${passesText(q)}. В сутках 24 ч. Сколько часов поставить за ${d}?`;
    case 'потерян_проход':
      return `Проходы идут не по порядку: ${passesText(q)}.${h ? ` Посчитано ${h} ч.` : ''} Верно?`;
    case 'две_бригады': {
      const o = q.facts.otherBrigade;
      const there = o ? shownHours(o.value, o.hours) : '';
      return `В этот день человек есть и в табеле бригады «${o?.name ?? 'другой бригады'}»${there ? `: там ${there} ч` : ''}.${h ? ` Здесь посчитано ${h} ч.` : ''} Сколько часов работы было в вашей бригаде?`;
    }
    default:
      return `${typeLabel(q.type)}. Проверьте день: что поставить в табель?`;
  }
}

/** Факт дня коротко — для строки списка: «вход 07:44, выхода нет». */
export function factShort(q: Question): string {
  const h = machine(q);
  switch (kindOf(q.type)) {
    case 'нет_выхода': {
      const t = firstPass(q, 'IN');
      return t ? `вход ${t}, выхода нет` : 'выхода нет';
    }
    case 'нет_входа': {
      const t = firstPass(q, 'OUT');
      return t ? `выход ${t}, входа нет` : 'входа нет';
    }
    case 'больше_24_ч':
      return 'за дату больше 24 ч';
    case 'потерян_проход':
      return h ? `не по порядку; посчитано ${h} ч` : 'проходы не по порядку';
    case 'две_бригады': {
      const o = q.facts.otherBrigade;
      const there = o ? shownHours(o.value, o.hours) : '';
      return o ? `и в «${o.name}»${there ? ` (${there} ч)` : ''}` : 'и в другой бригаде';
    }
    default:
      return typeLabel(q.type).toLowerCase();
  }
}

/** Ответ коротко: «указано 10 ч», «работы не было → 0 ч», «часы верны», «уточню позже». */
export function answerSummary(a: QuestionAnswer, format: HoursFormat | null, tzOffset: string): string {
  const h = a.hours != null ? `${hoursText(a.hours, format)} ч` : null;
  if (a.via === 'edit') return h ? `правка часов: ${h}` : 'правка часов';
  switch (a.option) {
    case 'later':
      return 'уточню позже';
    case 'accept':
      return h ? `часы верны → ${h}` : 'часы верны';
    case 'zero':
      return 'работы не было → 0 ч';
    case 'hours':
      return h ? `указано ${h}` : 'указаны часы';
    case 'time': {
      const at = a.statedAt ? ruDateTime(a.statedAt, tzOffset).slice(11) : '';
      const what = a.statedKind === 'вход' ? 'вход' : 'выход';
      return `${what} в ${at}${h ? ` → ${h}` : ''}`;
    }
  }
}

export const TIME_ERROR = 'Не понятно время. Напишите, например, 19:00 или 1900';

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * Время ответа (Q-27) — как его пишут на объекте: «19:00», «19.00», «19 00», «1900», «730», «19», «7:5» → `ЧЧ:ММ`
 * («7:5» — 07:05). Часы 0–23, минуты 0–59; иначе — что написать.
 */
export function parseTime(text: string): { time: string } | { error: string } {
  const s = text.trim();
  let h: string;
  let m: string;
  const split = /^(\d{1,2})\s*[:.,\s-]\s*(\d{1,2})$/.exec(s);
  if (split) {
    h = split[1] as string;
    m = split[2] as string;
  } else if (/^\d{1,4}$/.test(s)) {
    // Только цифры: «19» — часы; «730» — 7:30; «1900» — 19:00.
    h = s.length <= 2 ? s : s.slice(0, s.length - 2);
    m = s.length <= 2 ? '0' : s.slice(-2);
  } else {
    return { error: TIME_ERROR };
  }
  const hh = Number(h);
  const mm = Number(m);
  if (hh > 23 || mm > 59) return { error: TIME_ERROR };
  return { time: `${pad2(hh)}:${pad2(mm)}` };
}

/** «ЧЧ:ММ» → секунды от начала суток. */
export function clockSeconds(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 3600 + (m ?? 0) * 60;
}

/** Подпись варианта для ответа сразу нескольким: без чисел одного человека («Да, верно — 9 ч» → «Часы верны у всех»). */
export function bulkLabel(option: AnswerOption, type: string, fallback: string): string {
  switch (option) {
    case 'hours':
      return 'Указать часы всем';
    case 'accept':
      return kindOf(type) === 'две_бригады' ? 'Весь день в моей бригаде' : 'Да, часы верны у всех';
    case 'later':
      return 'Уточню позже';
    case 'time':
      return kindOf(type) === 'нет_входа' ? 'Вход у всех в …' : 'Выход у всех в …';
    default:
      return fallback;
  }
}

/** Дни очереди: человек и дата, от старой даты к новой, по ФИО. `pick` — что требует действия в этой роли. */
export function queueDays(
  questions: readonly Question[],
  pick: (q: Question) => boolean,
): { personId: string; date: string }[] {
  const seen = new Set<string>();
  const out: { personId: string; date: string }[] = [];
  for (const q of [...questions].filter(pick).sort(compareQuestions)) {
    const k = dayKey(q.personId, q.date);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ personId: q.personId, date: q.date });
  }
  return out;
}

/**
 * Текущие вопросы из ответа `409 STALE_STATE` на ответ или решение кадров: `details.current` — массив вопросов
 * (у правки ячейки там одна ячейка — это не сюда). Нет или другой формы — пусто.
 */
export function staleQuestions(details: unknown): Question[] {
  const cur = (details as { current?: unknown } | null | undefined)?.current;
  if (!Array.isArray(cur)) return [];
  return cur.filter(
    (x): x is Question =>
      typeof x === 'object' && x !== null && typeof (x as Question).id === 'number' && 'state' in x,
  );
}

/** Предупреждения предпросмотра словами (коды сервера `SHIFT_OVER_16H` …; неизвестный — как прислан). */
export const WARNING_TEXT: Readonly<Record<string, string>> = {
  SHIFT_OVER_16H: 'Смена длиннее 16 ч. По правилам объекта смена не длиннее 16 ч, проверьте время.',
  DAY_OVER_16H: 'Больше 16 ч за день — проверьте.',
  TWO_BRIGADES_SUM: 'Вместе с другой бригадой выходит больше, чем по проходам. Кадры увидят расхождение.',
};
export const warningText = (w: string) => WARNING_TEXT[w] ?? w;
